/**
 * pixel-layered リグの合成(docs/19 §4.2)の管理画面用の簡易版。
 *
 * ゲーム側(apps/web)と同じ規則だが、apps/web のコードは import できないので管理画面内に持つ。
 * 扱う規則:
 *   - 描画順(下→上)は content-schema の PIXEL_LAYER_ORDER
 *   - arms は body の、bracers は armor の付属レイヤー(アイテムの `art.companions`)
 *   - helmet 装備中は ears を隠す(helmet の `flags.showEars` が true なら表示)
 *   - block のフレームでは shield を weapon の直前(weapon 以外の全レイヤーより前)に描く
 *   - jab-1 / jab-2 / slash-2 / slash-3 では cape を最前面に描く
 * 扱わない規則: hair を頭のシルエットで切り抜く(プレビューの確認用途では省略)。
 */
import { PIXEL_LAYER_ORDER, type ItemDef } from '@paltyping/content-schema';

export const PREVIEW_FRAME = 'idle-0';
export const PIXEL_CELL = 64;

const CAPE_FRONT_FRAMES: readonly string[] = ['jab-1', 'jab-2', 'slash-2', 'slash-3'];

export interface PixelLayer {
  /** レイヤー名(PIXEL_LAYER_ORDER のどれか)。 */
  readonly layer: string;
  readonly itemId: string;
  readonly sheet: string;
  readonly atlas: string;
}

/** 装備(枠 → アイテムID)から、描く順に並べたレイヤーを作る。 */
export function pixelLayers(
  equipped: Readonly<Record<string, string | null | undefined>>,
  itemsById: ReadonlyMap<string, ItemDef>,
  frame: string = PREVIEW_FRAME,
): PixelLayer[] {
  const layers = new Map<string, PixelLayer>();
  let helmet: ItemDef | undefined;
  for (const [slot, itemId] of Object.entries(equipped)) {
    if (!itemId) continue;
    const item = itemsById.get(itemId);
    if (!item || item.art.kind !== 'atlas') continue;
    if (slot === 'helmet') helmet = item;
    layers.set(slot, {
      layer: slot,
      itemId: item.id,
      sheet: item.art.sheet,
      atlas: item.art.atlas,
    });
    for (const companion of item.art.companions ?? []) {
      layers.set(companion.slot, {
        layer: companion.slot,
        itemId: item.id,
        sheet: companion.sheet,
        atlas: companion.atlas,
      });
    }
  }
  if (helmet && helmet.art.kind === 'atlas' && helmet.art.flags?.showEars !== true) {
    layers.delete('ears');
  }

  let order: string[] = [...PIXEL_LAYER_ORDER];
  if (frame.startsWith('block')) {
    order = order.filter((name) => name !== 'shield');
    order.splice(order.indexOf('weapon'), 0, 'shield');
  }
  if (CAPE_FRONT_FRAMES.includes(frame)) {
    order = [...order.filter((name) => name !== 'cape'), 'cape'];
  }
  return order.flatMap((name) => {
    const layer = layers.get(name);
    return layer ? [layer] : [];
  });
}

/** 素材のパスを配信URLへ(`VITE_CONTENT_BASE` からの相対。絶対URLはそのまま)。 */
export function pixelAssetUrl(contentBase: string, path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${contentBase}${path.replace(/^\/+/, '')}`;
}

export interface FrameRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** アトラス JSON(TexturePacker の hash / array 形式)からフレームの位置を取る。 */
export function findFrame(atlas: unknown, name: string): FrameRect | null {
  const frames = (atlas as { frames?: unknown } | null)?.frames;
  let entry: unknown;
  if (Array.isArray(frames)) {
    entry = frames.find((value) => (value as { filename?: unknown }).filename === name);
  } else if (typeof frames === 'object' && frames !== null) {
    entry = (frames as Record<string, unknown>)[name];
  }
  const rect = (entry as { frame?: Partial<FrameRect> } | undefined)?.frame;
  if (
    !rect ||
    typeof rect.x !== 'number' ||
    typeof rect.y !== 'number' ||
    typeof rect.w !== 'number' ||
    typeof rect.h !== 'number'
  ) {
    return null;
  }
  return { x: rect.x, y: rect.y, w: rect.w, h: rect.h };
}
